/**
 * Streaming MP3 decoder via WebCodecs.AudioDecoder.
 *
 * The third leg of the OOM-free pipeline: read MP3 frames one at a time,
 * decode each via WebCodecs.AudioDecoder, hand back PCM chunks to the
 * caller for processing (gain, fade, EQ, reverse, speed). Combined with
 * MP3Encoder.encodeStream this lets us do `mp3 in → process → mp3 out`
 * at constant memory regardless of source duration — a 5-hour podcast
 * fits in the same heap as a 30-second clip.
 *
 * Why WebCodecs (not decodeAudioData)?
 *   AudioContext.decodeAudioData wants the WHOLE source buffer and emits
 *   a single AudioBuffer. That's the original cause of "stuck at 65%" —
 *   211 MB heap on a 5-min source. WebCodecs.AudioDecoder accepts one
 *   EncodedAudioChunk at a time and emits one AudioData per chunk; we
 *   never hold more than ~26 ms of PCM (~5 KB stereo @ 44.1k).
 *
 * Browser support (2026):
 *   Chrome 116+ (Aug 2023), Edge 116+: full mp3 codec support.
 *   Firefox: experimental, gated behind flag (not relied on here).
 *   Safari: no AudioDecoder yet.
 *
 * Caller checks `Mp3DecoderStream.isSupported()` before attempting; on
 * unsupported browsers fall back to legacy decodeAudioData (or the
 * frame-level paths in mp3-frame-trim.js when applicable).
 *
 * Usage:
 *
 *     if (!await Mp3DecoderStream.isSupported({sampleRate: 44100, channels: 2})) {
 *         // fall back...
 *     }
 *     await Mp3DecoderStream.decode(blob, {
 *         onChunk: (samples) => {
 *             // samples = { channelData: [Float32Array, Float32Array], sampleRate, numberOfFrames }
 *         },
 *         onProgress: (pct) => {},
 *         signal: AbortSignal,
 *     });
 *
 * Caller-side flow control: `onChunk` may return a Promise — decoder
 * pauses (awaits) before feeding the next frame. This back-pressure keeps
 * downstream encoders from being flooded.
 *
 * Memory: ~one frame (5 KB) + brief AudioData reference + the encoded
 * source bytes (read all at once for simplicity — TODO: switch to
 * Blob.slice chunked reads if source is huge).
 */
(function () {
    'use strict';

    var ID3V2_LEN = 10;

    /** Detect if the runtime can decode MP3 via WebCodecs at the given config. */
    async function isSupported(config) {
        if (typeof window.AudioDecoder === 'undefined') return false;
        try {
            var probe = await window.AudioDecoder.isConfigSupported({
                codec: 'mp3',
                sampleRate: (config && config.sampleRate) || 44100,
                numberOfChannels: (config && config.channels) || 2,
            });
            return !!(probe && probe.supported);
        } catch (e) {
            return false;
        }
    }

    /**
     * Stream-decode an MP3 blob. Calls onChunk(samples) for each decoded
     * AudioData. Awaits onChunk's return value if it's a Promise (back-pressure).
     */
    async function decode(blob, opts) {
        opts = opts || {};
        if (typeof opts.onChunk !== 'function') {
            throw new Error('Mp3DecoderStream.decode: onChunk callback required');
        }
        if (typeof window.AudioDecoder === 'undefined') {
            throw new Error('Mp3DecoderStream: WebCodecs.AudioDecoder unavailable');
        }
        if (!window.Mp3FrameTrim || typeof window.Mp3FrameTrim.indexFrames !== 'function') {
            throw new Error('Mp3DecoderStream: Mp3FrameTrim required for frame parsing');
        }

        var ab = await blob.arrayBuffer();
        var bytes = new Uint8Array(ab);

        // Skip ID3v2 if present (decoder doesn't expect it before frames).
        var startOff = window.Mp3FrameTrim.parseId3v2Length(bytes) || 0;
        var endOff = bytes.length;
        // Trim trailing ID3v1 (128 bytes 'TAG...').
        if (endOff >= 128
            && bytes[endOff - 128] === 0x54 && bytes[endOff - 127] === 0x41 && bytes[endOff - 126] === 0x47) {
            endOff -= 128;
        }

        /* The stream verdict, not "any frames found": the walk finds lookalike
           frames inside AAC and FLAC too, and this lane is picked by the .mp3
           NAME — a renamed AAC would decode into a few seconds of noise and be
           accepted (an mp3 header's length is not trusted enough to gate on).
           No stream → throw → the next tier, whose decoder reads the content. */
        var T = window.Mp3FrameTrim;
        var frames;
        if (typeof T.locateStream === 'function') {
            var st = T.locateStream(bytes, startOff, endOff);
            if (!st) throw new Error('Mp3DecoderStream: not an MPEG audio stream');
            frames = st.frames;
        } else {
            frames = T.indexFrames(bytes, startOff, endOff).frames;
        }
        if (frames.length === 0) throw new Error('Mp3DecoderStream: no MP3 frames');

        // Determine codec config from the first audio frame. WebCodecs needs
        // sampleRate + numberOfChannels at configure time.
        var firstHdr = window.Mp3FrameTrim.parseFrameHeader(bytes, frames[0].offset);
        if (!firstHdr) throw new Error('Mp3DecoderStream: malformed first frame');

        // Detect Xing/Info first-frame and skip it from the decode stream
        // (not real audio data).
        var firstAudio = 0;
        // Reuse Xing detection by sniff: scan first frame for 'Xing'/'Info'.
        var fLen = firstHdr.frameLength;
        var scanEnd = Math.min(frames[0].offset + fLen, frames[0].offset + 200, bytes.length);
        for (var i = frames[0].offset + 4; i < scanEnd - 3; i++) {
            if ((bytes[i] === 0x58 && bytes[i+1] === 0x69 && bytes[i+2] === 0x6E && bytes[i+3] === 0x67) ||
                (bytes[i] === 0x49 && bytes[i+1] === 0x6E && bytes[i+2] === 0x66 && bytes[i+3] === 0x6F)) {
                firstAudio = 1;
                break;
            }
        }

        // Channel count from header — mono if mode=11 (channel mode), stereo otherwise.
        // bytes[frameOffset+3] >> 6 holds the mode bits; 11=mono. Use it as decoder hint.
        var modeBits = (bytes[frames[0].offset + 3] >> 6) & 0x03;
        var numCh = (modeBits === 3) ? 1 : 2;

        // Backpressure queue + AudioDecoder instance.
        var decErr = null;
        var pendingPushes = []; // promises returned by onChunk (awaited at flush)
        // Serialize onChunk calls: WebCodecs.AudioDecoder.output is synchronous
        // but our onChunk returns a Promise (downstream encoder.feed awaits
        // worker round-trip). Without serialization, two onChunk calls overlap
        // and the encoder's "previous feed not yet resolved" guard rejects
        // the second one — surfaces as a window.onunhandledrejection toast.
        // Telemetry 2026-05-22 audit: 37 events/7d on audio-enhancer with
        // "Unhandled promise at mp3-encoder.js:596: MP3Encoder.encodeStream.feed:
        // previous feed not yet resolved". Fix serializes via a Promise chain:
        // each onChunk waits for the previous to resolve before running.
        var lastChunkPromise = Promise.resolve();
        // Also collect serialized promises in pendingPushes so the outer flow
        // can await Promise.all(pendingPushes) at the end. Attach a no-op
        // .catch to each pushed promise so a downstream rejection doesn't
        // surface as unhandledrejection before Promise.all gets to it; the
        // real error still propagates to Promise.all (rejection isn't
        // silenced — only the "no handler attached yet" warning is).
        // Chrome's WebCodecs MP3 decoder emits AudioData with format 's16-planar'
        // (Int16 samples, planar layout) — NOT 'f32-planar'. Copying s16 bytes
        // directly into a Float32Array reinterprets the bit pattern: e.g. an
        // int16 word `0x7F7F` lands in a float32 slot as ~3.4e+38 (Float32 MAX)
        // — bug #495 (Роза, 2026-05-22): "Файл включается только в предпрослушке.
        // При скачивании — тишина". With any IIR filter in the chain (highpass
        // biquad), one such huge sample makes the state NaN and stays NaN; the
        // LAME encoder writes NaN samples as silence. WAV input was unaffected
        // because decodeWavStream produces properly-normalized Float32 samples.
        //
        // The fix: pass `format: 'f32-planar'` to copyTo() — per WebCodecs spec,
        // this requests automatic in-browser conversion from the source format
        // (whatever it is — s16-planar / s32 / u8) to f32-planar. Works for all
        // codecs and future browsers without per-format branching.
        //
        // Defence in depth: bound-check the first chunk to detect a future
        // regression (e.g. browser drops conversion support) before we feed
        // garbage to downstream filters. Properly-normalised PCM is |v| ≤ 1
        // (modest overshoot is possible after lossy decode but never > 4).
        var _formatChecked = false;
        var dec = new window.AudioDecoder({
            output: function (audioData) {
                try {
                    var outChannels = [];
                    var nFrames = audioData.numberOfFrames;
                    for (var c = 0; c < audioData.numberOfChannels; c++) {
                        var dst = new Float32Array(nFrames);
                        audioData.copyTo(dst, { planeIndex: c, format: 'f32-planar' });
                        outChannels.push(dst);
                    }
                    if (!_formatChecked) {
                        _formatChecked = true;
                        var srcFmt = audioData.format || 'unknown';
                        // Scan the first plane for an out-of-range sample —
                        // if conversion silently no-op'd, the garbage shows
                        // up here BEFORE any filter has a chance to swallow
                        // it as NaN. Fail loud so callers fall through to
                        // the FFmpeg path instead of silently producing a
                        // silent file.
                        var ch0 = outChannels[0];
                        var pk = 0;
                        for (var i = 0; i < ch0.length; i++) {
                            var v = ch0[i];
                            if (v !== v) { pk = Infinity; break; }   // NaN
                            var av = v < 0 ? -v : v;
                            if (av > pk) pk = av;
                        }
                        if (pk > 4) {
                            audioData.close();
                            throw new Error('Mp3DecoderStream: format conversion failed — ' +
                                'first chunk peak=' + pk.toExponential(2) +
                                ' (srcFormat=' + srcFmt + '). Browser may not support ' +
                                'copyTo({format:"f32-planar"}); falling back to FFmpeg.');
                        }
                        try { if (window.__ae) window.__ae('feature_use',
                            {slug:'mp3-stream-decoder', f:'first_chunk_ok',
                             srcFormat: srcFmt, peak: +pk.toFixed(4)}); } catch(_){}
                    }
                    var sample = {
                        channelData: outChannels,
                        sampleRate: audioData.sampleRate,
                        numberOfFrames: nFrames,
                        timestamp: audioData.timestamp,
                    };
                    audioData.close();
                    // Chain — wait for the previous chunk's downstream work to
                    // settle before kicking off this one. opts.onChunk MAY
                    // return a Promise (back-pressure); we await it via the chain.
                    lastChunkPromise = lastChunkPromise.then(function(){
                        var r = opts.onChunk(sample);
                        return r && typeof r.then === 'function' ? r : undefined;
                    });
                    // Suppress "no handler attached" unhandledrejection by
                    // attaching an empty .catch to the chained promise. Outer
                    // Promise.all(pendingPushes) still observes any real error.
                    lastChunkPromise.catch(function(){});
                    pendingPushes.push(lastChunkPromise);
                } catch (e) {
                    decErr = e;
                }
            },
            error: function (e) { decErr = e; },
        });

        try {
            dec.configure({
                codec: 'mp3',
                sampleRate: firstHdr.sampleRate,
                numberOfChannels: numCh,
            });
        } catch (e) {
            throw new Error('Mp3DecoderStream: configure failed: ' + (e && e.message || e));
        }

        var totalFrames = frames.length - firstAudio;
        var processed = 0;
        var sampleTimestamp = 0; // microseconds; used as monotonic ts for EncodedAudioChunk
        var samplesPerFrame = firstHdr.samples;
        var sourceSampleRate = firstHdr.sampleRate;

        for (var fi = firstAudio; fi < frames.length; fi++) {
            if (opts.signal && opts.signal.aborted) {
                try { dec.close(); } catch (_) {}
                throw Object.assign(new Error('Mp3DecoderStream: aborted'), { code: 'ABORTED' });
            }
            if (decErr) {
                try { dec.close(); } catch (_) {}
                throw new Error('Mp3DecoderStream: decoder error: ' + (decErr.message || decErr));
            }
            var fr = frames[fi];
            var fNext = (fi + 1 < frames.length) ? frames[fi + 1].offset : endOff;
            var slice = bytes.subarray(fr.offset, fNext);
            try {
                dec.decode(new window.EncodedAudioChunk({
                    type: 'key',
                    timestamp: sampleTimestamp,
                    data: slice,
                }));
            } catch (e) {
                try { dec.close(); } catch (_) {}
                throw new Error('Mp3DecoderStream: decode call failed at frame ' + fi + ': ' + (e && e.message || e));
            }
            sampleTimestamp += Math.round(samplesPerFrame * 1e6 / sourceSampleRate);
            processed++;
            if (typeof opts.onProgress === 'function' && (processed % 64 === 0)) {
                try { opts.onProgress(processed / totalFrames); } catch (_) {}
            }
        }

        // Flush — decoder produces any remaining queued AudioData in `output`.
        try { await dec.flush(); } catch (e) {
            try { dec.close(); } catch (_) {}
            throw new Error('Mp3DecoderStream: flush failed: ' + (e && e.message || e));
        }
        try { dec.close(); } catch (_) {}

        // Wait for any outstanding back-pressure promises caller returned.
        if (pendingPushes.length > 0) await Promise.all(pendingPushes);

        if (decErr) throw new Error('Mp3DecoderStream: decoder error: ' + (decErr.message || decErr));

        if (typeof opts.onProgress === 'function') {
            try { opts.onProgress(1.0); } catch (_) {}
        }

        return {
            framesDecoded: totalFrames,
            sampleRate: sourceSampleRate,
            channels: numCh,
            sourceBitrate: firstHdr.bitrate,
        };
    }

    window.Mp3DecoderStream = {
        isSupported: isSupported,
        decode: decode,
    };
})();
